import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStudioUser } from '../hooks/useStudioUser';
import { PercentBadgeIcon, BanknotesIcon } from '@heroicons/react/24/outline';

interface FamilyDiscount {
    student_id: string;
    student_name: string;
    student_position: number;
    discount_type: 'percentage' | 'fixed_amount';
    discount_value: number;
    rule_id: string;
}

interface FamilyDiscountDisplayProps {
    guardianId: string;
    refreshTrigger?: number; // Add a refresh trigger prop
}

const FamilyDiscountSkeleton: React.FC = () => (
    <div className="animate-pulse space-y-3" aria-busy="true" aria-label="Loading family discounts">
        <div className="flex items-center space-x-2">
            <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="space-y-2">
            {[...Array(2)].map((_, i) => (
                <div
                    key={i}
                    className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2"
                >
                    <div className="flex items-center space-x-2">
                        <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                    </div>
                    <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                </div>
            ))}
        </div>
    </div>
);

const FamilyDiscountDisplay: React.FC<FamilyDiscountDisplayProps> = ({ guardianId, refreshTrigger }) => {
    const { getAuthToken } = useAuth();
    const { studioUser } = useStudioUser();
    const [discounts, setDiscounts] = useState<FamilyDiscount[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchDiscounts = useCallback(async () => {
        const authToken = getAuthToken();
        if (!authToken || !studioUser?.studio_id || !guardianId) {
            // Keep the skeleton up until auth/studio/family are ready to fetch.
            setLoading(true);
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/calculate/${guardianId}`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        studioId: studioUser.studio_id
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                setDiscounts(data.discounts || []);
            } else {
                setDiscounts([]);
            }
        } catch (error) {
            console.error('Error fetching family discounts:', error);
            setDiscounts([]);
        } finally {
            setLoading(false);
        }
    }, [guardianId, getAuthToken, studioUser?.studio_id]);

    useEffect(() => {
        fetchDiscounts();
    }, [fetchDiscounts, refreshTrigger]);

    const getOrdinalSuffix = (num: number): string => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return 'st';
        if (j === 2 && k !== 12) return 'nd';
        if (j === 3 && k !== 13) return 'rd';
        return 'th';
    };

    if (loading) {
        return <FamilyDiscountSkeleton />;
    }

    if (discounts.length === 0) {
        return (
            <div className="text-sm text-gray-500 dark:text-gray-400">
                No family discounts active. Assign a membership to students in this family to automatically apply discounts.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <PercentBadgeIcon className="h-4 w-4 mr-2 text-green-500" />
                Active Family Discounts
            </h4>
            
            <div className="space-y-2">
                {discounts.map((discount) => (
                    <div
                        key={discount.student_id}
                        className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
                    >
                        <div className="flex items-center space-x-2">
                            {discount.discount_type === 'percentage' ? (
                                <PercentBadgeIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                            ) : (
                                <BanknotesIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                            )}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {discount.student_name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({discount.student_position}{getOrdinalSuffix(discount.student_position)} student)
                            </span>
                        </div>
                        
                        <div className="text-sm font-semibold text-green-700 dark:text-green-400">
                            {discount.discount_type === 'percentage' 
                                ? `${discount.discount_value}% off`
                                : `$${discount.discount_value.toFixed(2)} off`
                            }
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FamilyDiscountDisplay;
